import { FC, useMemo } from "react";
import { CellProps, Column } from "react-table";
import { format, subMonths } from "date-fns";
import { pt } from "date-fns/locale";
import { Center } from "@chakra-ui/react";
import { ViewIcon } from "@chakra-ui/icons";
import { primaryColor } from "../../../utils/colors";
import TableComponent from "../../../components/organism/Table/TableComponent";
import IndicatorVariationStateColor from "../component/IndicatorVariationStateColor";
import IndicatorState from "../component/IndicatorState";

interface TableRegionProps {
  dataTable: any[];
  isLoaded: boolean;
  frequency: number;
  setValue: (key: string, value: any) => void;
  openModal?: (newTitle: string, newButtonTitle: string) => void;
  date?: Date | null | undefined;
}

const TableRegion: FC<TableRegionProps> = ({
  dataTable,
  isLoaded,
  frequency,
  setValue,
  openModal,
  date,
}) => {
  const getFormattedMonthYear = (date: Date) =>
    format(date, "MMMM yyyy", { locale: pt });

  const getFormattedYear = (date: Date) => format(date, "yyyy", { locale: pt });

  const getFormattedSemester = (date: Date) => {
    const month = date.getMonth() + 1;
    const semester = month <= 6 ? "1º Semestre" : "2º Semestre";
    return `${semester} ${getFormattedYear(date)}`;
  };

  const getFormattedTrimester = (date: Date) => {
    const month = date.getMonth() + 1;
    let trimester;

    if (month <= 3) {
      trimester = "1º Trimestre";
    } else if (month <= 6) {
      trimester = "2º Trimestre";
    } else if (month <= 9) {
      trimester = "3º Trimestre";
    } else {
      trimester = "4º Trimestre";
    }

    return `${trimester} ${getFormattedYear(date)}`;
  };

  let currentValue: string;
  let lastValue: string;
  let lastLastValue: string;
  let currentWithPreviousYear: string;
  let currentWithPrevious: string;

  const currentDate = date ?? new Date();

  if (frequency === 3) {
    currentValue = getFormattedMonthYear(currentDate);
    lastValue = getFormattedMonthYear(subMonths(currentDate, 1));
    lastLastValue = getFormattedMonthYear(subMonths(currentDate, 12));
    currentWithPreviousYear = "% Anual";
    currentWithPrevious = "% Mensal";
  } else if (frequency === 4) {
    currentValue = getFormattedTrimester(currentDate);
    lastValue = getFormattedTrimester(subMonths(currentDate, 3));
    lastLastValue = getFormattedTrimester(subMonths(currentDate, 12));
    currentWithPreviousYear = "% Anual";
    currentWithPrevious = "% Trimestral";
  } else if (frequency === 5) {
    currentValue = getFormattedSemester(currentDate);
    lastValue = getFormattedSemester(subMonths(currentDate, 6));
    lastLastValue = getFormattedSemester(subMonths(currentDate, 12));
    currentWithPreviousYear = "% Anual";
    currentWithPrevious = "% Semestral";
  } else if (frequency === 6) {
    currentValue = getFormattedYear(currentDate);
    lastValue = getFormattedYear(subMonths(currentDate, 12));
    lastLastValue = getFormattedYear(subMonths(currentDate, 24));
    currentWithPreviousYear = "% Últimos dois anos";
    currentWithPrevious = "% Anual";
  }

  const columnsStyle = [
    "lastYearValue.value",
    "lastValue.value",
    "currentValue.value",
    "currentWithPreviousYear.value",
    "currentWithPrevious.value",
  ];

  const columns = useMemo(() => {
    const baseColumns: Column[] = [
      { Header: "Indicador", accessor: "indicator.name" },
      { Header: "Sub-sistema", accessor: "indicator.subSystem.name" },
      { Header: "Tipo de indicador", accessor: "indicator.type.name" },
      { Header: "Métrica", accessor: "indicator.metric.name" },
      {
        Header: lastLastValue,
        disableSortBy: true,
        accessor: "lastYearValue.value",
        Cell: ({ value }: any) => value?.toFixed(2) ?? "-",
      },
      {
        Header: lastValue,
        disableSortBy: true,
        accessor: "lastValue.value",
        Cell: ({ value }: any) => value?.toFixed(2) ?? "-",
      },
      {
        Header: currentValue,
        disableSortBy: true,
        accessor: "currentValue.value",
        Cell: ({ value }: any) => value?.toFixed(2) ?? "-",
      },
      {
        Header: "Estado do indicador",
        disableSortBy: true,
        accessor: "state",
        Cell: ({ value }: any) => {
          return value ? <IndicatorState stateValue={value} /> : "-";
        },
      },
      {
        Header: "Variações",
        disableSortBy: true,
        columns: [
          {
            Header: currentWithPreviousYear,
            accessor: "currentWithPreviousYear.value",
            disableSortBy: true,
            Cell: ({ row }: any) => {
              return (
                <IndicatorVariationStateColor
                  state={row.original.currentWithPreviousYear?.status ?? null}
                  variation={
                    row.original.currentWithPreviousYear?.variaton ?? null
                  }
                />
              );
            },
          },
          {
            Header: currentWithPrevious,
            disableSortBy: true,
            accessor: "currentWithPrevious.value",
            Cell: ({ row }: any) => {
              return (
                <IndicatorVariationStateColor
                  state={row.original.currentWithPrevious?.status ?? null}
                  variation={row.original.currentWithPrevious?.variaton ?? null}
                />
              );
            },
          },
        ],
      },
    ];

    if (openModal) {
      baseColumns.push({
        Header: "Ver Detalhes",
        accessor: "ver",
        disableSortBy: true,
        Cell: ({ row }: CellProps<any, string>) => (
          <Center>
            <ViewIcon
              color={primaryColor}
              boxSize={6}
              mr="10px"
              cursor="pointer"
              onClick={() => {
                openModal(
                  `Ver detalhes | ${row.original.indicator.name}`,
                  "Fechar",
                );
                setValue("row", row.original);
              }}
            />
          </Center>
        ),
      });
    }

    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, openModal]);

  return (
    <TableComponent
      isLoaded={isLoaded}
      columns={columns}
      data={dataTable}
      columnsStyle={columnsStyle}
      fixedColumnIds={[{ value: "ver", hasShadow: true }]}
    />
  );
};

export default TableRegion;
